/**
 * Load one thing from the server and report the three states a screen has to draw.
 *
 * No cache and no refetch-on-focus: the server already holds the replies for a minute, and the
 * book does not change while a reader is looking at it. A request that lands after the screen
 * has moved on is dropped rather than written into a page that is no longer there.
 *
 * The caller passes a `key` that names what it wants — the work is re-run when the key changes
 * and not because a closure was rebuilt on a render.
 */

import { useEffect, useRef, useState } from "react";

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

export function useAsync<T>(key: string, work: () => Promise<T>): Loaded<T> {
  const latest = useRef(work);
  latest.current = work;
  const [state, setState] = useState<Loaded<T>>({ data: null, error: null, loading: true });

  useEffect(() => {
    let live = true;
    setState({ data: null, error: null, loading: true });
    latest
      .current()
      .then((data) => {
        if (live) setState({ data, error: null, loading: false });
      })
      .catch((cause: Error) => {
        if (live) setState({ data: null, error: cause.message, loading: false });
      });
    return () => {
      live = false;
    };
  }, [key]);

  return state;
}
